import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import classnames from 'classnames';

import { useHideModal } from '../../actions/modal';
import MaterialTextField from '../../components/MaterialTextField/MaterialTextField';
import { Panel } from '../../components/Panel/Panel';
import { ReportDropzoneImage } from '../Report/ReportDropzoneImage/ReportDropzoneImage';
import Pdf from '../../icons/image/Pdf';
import { showPdf } from '../../actions/documents';
import { useStyles } from './useStyles';
import { DialogAdaptive, DialogContent, DialogTitle } from '../../components/material-ui/Dialog';
import { AppBar } from '../../components/material-ui/AppBar';
import { Toolbar } from '../../components/material-ui/Toolbar';
import { Typography } from '../../components/material-ui/Typography';
import { Button } from '../../components/material-ui/Button';
import { preloadAllReportImageUrl } from '../../actions/reports';

export const ReportDetail = React.forwardRef(({ report }, ref) => {
    const {
        generalInfo,
        addInfo,
        value,
        images,
        flightName,
        flightDate,
        number,
        createdDate,
        statusDate,
        statusComment,
        statusType,
        safetyTypeName,
        safetyGroupTypeName,
        airportName,
        feedbacks = [],
    } = report;
    const { t: translate } = useTranslation();
    const flight = translate('reports.flightNameDateTime', {
        flightName,
        datetime: flightDate && moment.utc(flightDate).format('DD.MM.YYYY HH:mm'),
    });
    const safetyType = `${safetyTypeName} (${safetyGroupTypeName})`;
    const hideModal = useHideModal();
    const dispatch = useDispatch();
    const classes = useStyles();
    React.useEffect(() => {
        dispatch(preloadAllReportImageUrl());
    }, []);

    const handleShowPdf = React.useCallback((id) => {
        dispatch(showPdf(id, null, ''));
    }, []);
    const InputProps = React.useMemo(
        () => ({
            classes: {
                input: classes.field,
            },
        }),
        []
    );

    return (
        <DialogAdaptive open={true} fullScreen={true} scroll="paper">
            <DialogTitle>
                <AppBar>
                    <Toolbar>
                        <Typography variant="h6" className={classes.title} color="inherit">
                            {translate('reports.reportDetails')}
                        </Typography>
                        <Button color="inherit" onClick={hideModal}>
                            {translate('ok')}
                        </Button>
                    </Toolbar>
                </AppBar>
            </DialogTitle>
            <DialogContent className={classes.content} ref={ref}>
                <Panel title={translate('reports.reportInfo')} className={classes.info}>
                    <div className={classes.inner}>
                        <MaterialTextField
                            label={translate('flight')}
                            defaultValue={flight}
                            className={classes.field}
                            InputProps={InputProps}
                            fullWidth={true}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('labels.createdAt')}
                            defaultValue={createdDate && moment.utc(createdDate).format('DD.MM.YYYY HH:mm')}
                            className={classnames(classes.field, classes.fieldHalf)}
                            InputProps={InputProps}
                            fullWidth={true}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('reports.report')}
                            defaultValue={number}
                            className={classnames(classes.field, classes.fieldHalf)}
                            InputProps={InputProps}
                            fullWidth={true}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('reports.reportTypeGroup')}
                            defaultValue={safetyType}
                            className={classes.field}
                            InputProps={InputProps}
                            fullWidth={true}
                            multiline={true}
                            maxRows={2}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('airport')}
                            defaultValue={airportName}
                            fullWidth={true}
                            multiline={true}
                            className={classnames(classes.field, classes.fieldHalf)}
                            InputProps={InputProps}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('value')}
                            defaultValue={value}
                            className={classnames(classes.field, classes.fieldHalf)}
                            InputProps={InputProps}
                            fullWidth={true}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('reports.basicInfo')}
                            defaultValue={generalInfo}
                            className={classes.textarea}
                            InputProps={InputProps}
                            fullWidth={true}
                            multiline={true}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('labels.additionalInfo')}
                            defaultValue={addInfo}
                            className={classes.textarea}
                            InputProps={InputProps}
                            fullWidth={true}
                            multiline={true}
                            disabled={true}
                        />
                    </div>
                </Panel>
                {feedbacks.map(({ safetyFeedbackId, receiptDate, dayCount, closedDate, responsible, reason, actions }) => (
                    <Panel key={safetyFeedbackId} title={translate('reports.feedbackInfo')} className={classes.info}>
                        <div className={classes.inner}>
                            <MaterialTextField
                                label={translate('reports.dateReceived')}
                                defaultValue={receiptDate && moment.utc(receiptDate).format('DD.MM.YYYY HH:mm')}
                                className={classnames(classes.field, classes.fieldHalf)}
                                InputProps={InputProps}
                                fullWidth={true}
                                disabled={true}
                            />
                            <MaterialTextField
                                label={translate('reports.daysToProcess')}
                                defaultValue={dayCount}
                                className={classnames(classes.field, classes.fieldHalf)}
                                InputProps={InputProps}
                                fullWidth={true}
                                disabled={true}
                            />
                            <MaterialTextField
                                label={translate('reports.dateClosed')}
                                defaultValue={closedDate && moment.utc(closedDate).format('DD.MM.YYYY HH:mm')}
                                className={classes.field}
                                InputProps={InputProps}
                                fullWidth={true}
                                disabled={true}
                            />
                            <MaterialTextField
                                label={translate('reports.responsiblePersonDepartment')}
                                defaultValue={responsible}
                                className={classes.field}
                                InputProps={InputProps}
                                fullWidth={true}
                                multiline={true}
                                disabled={true}
                            />
                            <MaterialTextField
                                label={translate('reports.reason')}
                                defaultValue={reason}
                                className={classes.field}
                                InputProps={InputProps}
                                fullWidth={true}
                                multiline={true}
                                disabled={true}
                            />
                            <MaterialTextField
                                label={translate('reports.actions')}
                                defaultValue={actions}
                                className={classes.field}
                                InputProps={InputProps}
                                fullWidth={true}
                                multiline={true}
                                disabled={true}
                            />
                        </div>
                    </Panel>
                ))}
                <Panel title={translate('reports.reportStatus')} className={classes.info}>
                    <div className={classes.inner}>
                        <MaterialTextField
                            label={translate('reports.statusDate')}
                            defaultValue={statusDate && moment.utc(statusDate).format('DD.MM.YYYY HH:mm')}
                            className={classnames(classes.field, classes.fieldHalf)}
                            InputProps={InputProps}
                            fullWidth={true}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('reports.status')}
                            defaultValue={statusType}
                            className={classnames(classes.field, classes.fieldHalf)}
                            InputProps={InputProps}
                            fullWidth={true}
                            disabled={true}
                        />
                        <MaterialTextField
                            label={translate('comment')}
                            defaultValue={statusComment}
                            className={classes.field}
                            InputProps={InputProps}
                            fullWidth={true}
                            multiline={true}
                            disabled={true}
                        />
                    </div>
                </Panel>

                <Panel title={translate('labels.attachments')} className={classnames(classes.info, classes.attachments)}>
                    <div className={classnames(classes.inner, classes.images)}>
                        {images.length === 0 ? (
                            <Typography>{translate('messages.noAttachments')}</Typography>
                        ) : (
                            images
                                .filter((img) => !img.isRemoved)
                                .map((img, index) => {
                                    return img.url ? (
                                        <ReportDropzoneImage
                                            key={index}
                                            className={classes.preview}
                                            height={150}
                                            src={img.url}
                                            quickShow={true}
                                        />
                                    ) : (
                                        <Pdf
                                            key={index}
                                            width={80}
                                            height={80}
                                            className={classes.preview}
                                            onClick={() => handleShowPdf(`si_${img.id}`)}
                                        />
                                    );
                                })
                        )}
                    </div>
                </Panel>
            </DialogContent>
        </DialogAdaptive>
    );
});
